import yaml from 'js-yaml';
import {
  DIVERSION_DISPLAY_STATES,
  DIVERSION_POLICIES,
  DIVERSION_STATUSES,
  DIVERSION_YAML_SEPARATOR,
  UNACKNOWLEDGED_THRESHOLD_MINUTES,
} from '../constants/Values';
import { ABORTED } from '../constants/States';

/**
 * Derive the client-side display state for a diversion. The server tracks
 * raw status (PENDING/DIVERTING/SHELTERED/BACK_ON_ROUTE) and routeState
 * (DIVERTED/ABORTED/etc.) separately; the UI surfaces three additional
 * derived states layered on top:
 *
 *   - CANCELLATION_PENDING — diversion.cancelledAt != null. Highest precedence.
 *   - PREMATURE_ABORT      — routeState === 'ABORTED' AND status !== 'SHELTERED'
 *                            (driver bailed before reaching shelter).
 *   - UNACKNOWLEDGED       — status === 'PENDING' AND age > 5 min.
 *
 * Precedence order: CANCELLATION_PENDING > PREMATURE_ABORT > UNACKNOWLEDGED >
 * raw status. The routeState=ABORTED + status=SHELTERED case is the expected
 * end-of-shelter flow and stays SHELTERED (NOT premature).
 *
 * @param {Object|null|undefined} diversion - The activeRoute.diversion block.
 *   Returns null if diversion is null/undefined.
 * @param {string} routeState - The route's RouteState ('DIVERTED', 'ABORTED', etc.).
 * @param {Date|number} now - Current time, for the UNACKNOWLEDGED age check.
 *   Date or epoch-ms. Pass new Date() in production; pass a fixed value in tests.
 * @returns {string|null} One of DIVERSION_DISPLAY_STATES for all derived states
 *   (CANCELLATION_PENDING, PREMATURE_ABORT, UNACKNOWLEDGED, PENDING). For
 *   DIVERTING and SHELTERED the raw diversion.status string is returned — those
 *   keys are shared between DIVERSION_STATUSES and DIVERSION_DISPLAY_STATES so
 *   the value is identical. For BACK_ON_ROUTE the raw status string is returned
 *   directly (BACK_ON_ROUTE is not in DIVERSION_DISPLAY_STATES because the
 *   diversion block goes null on the next refetch before any banner renders it,
 *   but the derivation is honest). Returns null when diversion is null/undefined.
 */
export function deriveDiversionDisplayState(diversion, routeState, now) {
  if (!diversion) return null;
  if (diversion.cancelledAt) return DIVERSION_DISPLAY_STATES.CANCELLATION_PENDING;

  // Driver aborted the route before reaching the Safe Zone — escalate.
  // Expected-flow abort (driver sheltered then aborted) is NOT premature.
  if (routeState === ABORTED && diversion.status !== DIVERSION_STATUSES.SHELTERED) {
    return DIVERSION_DISPLAY_STATES.PREMATURE_ABORT;
  }

  if (diversion.status === DIVERSION_STATUSES.PENDING) {
    const createdMs = new Date(diversion.createdDate).getTime();
    const nowMs = now instanceof Date ? now.getTime() : now;
    const minsSince = (nowMs - createdMs) / 60000;
    return minsSince > UNACKNOWLEDGED_THRESHOLD_MINUTES
      ? DIVERSION_DISPLAY_STATES.UNACKNOWLEDGED
      : DIVERSION_DISPLAY_STATES.PENDING;
  }

  // DIVERTING / SHELTERED / BACK_ON_ROUTE pass through to the same-named
  // display state (the maps share keys for these). BACK_ON_ROUTE is terminal
  // server-side — the diversion block goes null on the next refetch — so the
  // banner never actually renders this state, but the derivation is honest.
  return diversion.status;
}

/**
 * Split a notification body into its preamble and YAML payload.
 * Splits on the FIRST occurrence of '\n---' — uses indexOf+substring,
 * NOT String.prototype.split (which would fragment a preamble that
 * legitimately contains '\n---' as a markdown horizontal rule).
 *
 * Mirrors the server-side parser at:
 *   educe-tec/.../transportation/notification/parser/DiversionPayloadParser.java:66
 *
 * The returned yamlText starts with '---' (the leading newline is consumed
 * by the separator, the '---' YAML document-start marker is preserved) —
 * pass straight to yaml.safeLoad in parseDiversionYaml.
 *
 * @param {string|null|undefined} body - The notification body.
 * @returns {[string, string]} [preamble, yamlText]. If no separator is found,
 *   returns [body, '']. If body is null/undefined, returns ['', ''].
 */
export function splitPreambleAndYaml(body) {
  if (body == null) return ['', ''];
  const idx = body.indexOf(DIVERSION_YAML_SEPARATOR);
  if (idx < 0) return [body, ''];
  // idx points at the '\n' before '---'. The preamble is everything before
  // that newline; the yamlText starts at idx + 1 (the '-' of '---'),
  // preserving the YAML document-start marker. Mirrors server-side
  // DiversionPayloadParser.java:66+75 (substring(idx + 1)).
  return [body.substring(0, idx), body.substring(idx + 1)];
}

/**
 * Parse the YAML half of a DIVERSION notification body into a payload object.
 * Tolerant: returns {} on null, empty, or malformed input rather than throwing
 * (lets callers null-check the fields they care about).
 *
 * Expected shapes:
 *   - Create: { policy: 'NEAREST'|'FASTEST'|'NONE', shelters: [{location, coords?, address?, placeId?}, ...] }
 *   - Cancel: { cancel: true, notificationId?: string }
 *
 * @param {string} yamlText - The YAML text returned by splitPreambleAndYaml.
 *   Safe to pass with leading '---'.
 * @returns {Object} Parsed object, or {} on any parse failure.
 */
export function parseDiversionYaml(yamlText) {
  if (!yamlText) return {};
  try {
    const parsed = yaml.safeLoad(yamlText);
    // safeLoad returns undefined for empty / whitespace-only input.
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // YAMLException — malformed payload. Return {} so callers can null-check
    // fields without wrapping every read in try/catch. Spec D4 also says
    // the rendering layer falls back to raw body on parse failure.
    return {};
  }
}

/**
 * Render a one-line summary of a shelter list for the notice-inbox row.
 * Spec format:
 *   - 0 shelters: ''
 *   - 1 shelter:  'Foo'
 *   - 2 shelters: 'Foo, Bar'
 *   - 3+:         'Foo, Bar, +N more'  (where N = total - 2)
 *
 * Caller is responsible for prefixing with 'Diversion — ' or i18n equivalent —
 * this helper only formats the names list.
 *
 * @param {Array<{location: string}>|null|undefined} shelters
 * @returns {string}
 */
export function formatShelterListSummary(shelters) {
  if (!shelters || shelters.length === 0) return '';
  if (shelters.length === 1) return shelters[0].location;
  if (shelters.length === 2) return `${shelters[0].location}, ${shelters[1].location}`;
  const more = shelters.length - 2;
  return `${shelters[0].location}, ${shelters[1].location}, +${more} more`;
}

/**
 * Inverse of DiversionSubform.serializeBody — given a raw notification body
 * string (preamble + '\n---' + YAML), return the DiversionSubform value-shape
 * object the composer expects. Used by NotificationForm.templateSeed
 * (task 06) when a TM picks a saved DIVERSION template from the sidebar:
 * the template's stored body is a string, but DiversionSubform's `value` prop
 * needs { preamble, shelters, policy, cancel }.
 *
 * Composes the existing splitPreambleAndYaml + parseDiversionYaml helpers.
 * Tolerant: falls back to subform default-shape values for any missing /
 * malformed YAML fields, so a corrupted template never crashes the composer.
 *
 * @param {string|null|undefined} body - The template body string.
 * @returns {{ preamble: string, shelters: Array, policy: string, cancel: boolean }}
 *   Always returns a valid subform value-shape — never null/undefined.
 *   Defaults: preamble='', shelters=[], policy='NEAREST', cancel=false.
 */
export function bodyToSubformState(body) {
  const defaults = {
    preamble: '',
    shelters: [],
    policy: DIVERSION_POLICIES.NEAREST,
    cancel: false,
  };
  if (!body) return defaults;
  const [preamble, yamlText] = splitPreambleAndYaml(body);
  const parsed = parseDiversionYaml(yamlText);
  return {
    preamble: preamble || '',
    shelters: Array.isArray(parsed.shelters) ? parsed.shelters : [],
    // Server tolerates missing policy on cancel templates; default keeps the
    // subform radios in a valid state.
    policy: parsed.policy || defaults.policy,
    cancel: parsed.cancel === true,
  };
}
